import { isClient, isServer } from '@mr-yum/frontend-core/dist/support/env'
import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react'
import amplitude from 'amplitude-js'
import {
  FLAG_ALLOW_POSITIONING_INFO_BLOCKS,
  FLAG_LOGIN_AS_ANON_GUEST,
  FLAG_MODIFYING_UPSELLS,
  FLAG_MULTIPLE_MODIFIERS,
  FLAG_NEW_REDEEM_POINTS_VALUE,
  FLAG_ONE_CLICK_PAYMENTS,
  FLAG_POINTS_TO_USE_POLLING,
  FLAG_REORDER_DRINKS_V2_BASIC,
  FLAG_REWARDS_BALANCE,
  FLAG_REWARDS_PRICE,
  FLAG_SPLIT_BY_SELECTED_ITEMS,
} from 'common/constants'
import { config } from 'lib/config'
import { GuestVenue, Maybe, useCurrentUserQuery } from 'lib/gql'
import { getOrderingTypeFromSlug, OrderingTypeSlug } from 'lib/routes'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import { Amplitude, AmplitudeProvider } from 'react-amplitude-hooks'

const amplitudeInstance = isClient ? amplitude.getInstance() : undefined

export const AmplitudeTracking: React.FC<{
  venueSlug: string | null
  orderingTypeSlug: OrderingTypeSlug | null
  guestVenue?: Maybe<Pick<GuestVenue, 'id' | 'organizationId'>>
}> = ({ children, venueSlug, orderingTypeSlug, guestVenue }) => {
  const { flagsReady } = useFlagsStatus()
  const { query } = useRouter()

  const splitBySelectedItems = useFlag(FLAG_SPLIT_BY_SELECTED_ITEMS)
  const rewardsPrice = useFlag(FLAG_REWARDS_PRICE)
  const multipleModifiers = useFlag(FLAG_MULTIPLE_MODIFIERS)
  const reorderDrinksV2Basic = useFlag(FLAG_REORDER_DRINKS_V2_BASIC)
  const oneClickPayments = useFlag(FLAG_ONE_CLICK_PAYMENTS)
  const rewardsBalance = useFlag(FLAG_REWARDS_BALANCE)
  const modifyingUpsells = useFlag(FLAG_MODIFYING_UPSELLS)
  const allowPositioningOfInfoBlocks = useFlag(
    FLAG_ALLOW_POSITIONING_INFO_BLOCKS,
  )
  const newRedeemPointsValue = useFlag(FLAG_NEW_REDEEM_POINTS_VALUE)
  const pointsToUseShouldPoll = useFlag(FLAG_POINTS_TO_USE_POLLING)
  const loginAsAnonGuest = useFlag(FLAG_LOGIN_AS_ANON_GUEST)

  const [{ data }] = useCurrentUserQuery({
    pause: isServer,
  })

  const currentUser = data?.currentUser
  const userId = currentUser?.id || undefined

  /**
   * Feature Flags
   */
  useEffect(() => {
    if (flagsReady) {
      amplitudeInstance?.setUserProperties({
        splitBySelectedItems,
        rewardsPrice,
        multipleModifiers,
        reorderDrinksV2Basic,
        oneClickPayments,
        rewardsBalance,
        modifyingUpsells,
        allowPositioningOfInfoBlocks,
        newRedeemPointsValue,
        pointsToUseShouldPoll,
        loginAsAnonGuest,
      })
    }
  }, [
    flagsReady,
    multipleModifiers,
    oneClickPayments,
    reorderDrinksV2Basic,
    rewardsBalance,
    rewardsPrice,
    splitBySelectedItems,
    modifyingUpsells,
    allowPositioningOfInfoBlocks,
    newRedeemPointsValue,
    pointsToUseShouldPoll,
    loginAsAnonGuest,
  ])

  return (
    <AmplitudeProvider
      amplitudeInstance={amplitudeInstance || {}}
      apiKey={config.amplitudeKey}
      userId={userId}
      config={{
        includeReferrer: true,
        includeFbclid: true,
        includeGclid: true,
        includeUtm: true,
      }}
    >
      <Amplitude
        eventProperties={{
          venue: {
            venueSlug: venueSlug,
            venueId: guestVenue?.id,
            organizationId: guestVenue?.organizationId,
            orderingType: orderingTypeSlug
              ? getOrderingTypeFromSlug(orderingTypeSlug)
              : undefined,
            category: query.category,
          },
        }}
      >
        {children}
      </Amplitude>
    </AmplitudeProvider>
  )
}
